import React, { useReducer, useState, useEffect } from 'react';
import { ArrowLeft, Home, Thermometer, CloudRain, Calendar, MapPin } from 'lucide-react';
import { MovePreferences, LocationResult, PerfectConditions } from '../types/weather';
import { LocationComparison } from './LocationComparison';
import { LocationTypeahead } from './LocationTypeahead';
import { movePlanningApi } from '../services/movePlanningApi';
import { LocationSuggestion } from '../services/locationSearchApi';

interface PlanningMoveFlowProps {
  onBack: () => void;
  onLoading: (loading: boolean) => void;
  onError: (error: string | null) => void;
}

// State management with useReducer for atomic updates
interface FlowState {
  step: 'preferences' | 'locations' | 'results';
  preferences: MovePreferences;
  results: LocationResult[];
  validatedLocations: LocationSuggestion[]; // Store validated location data
}

type FlowAction = 
  | { type: 'SET_STEP'; payload: FlowState['step'] }
  | { type: 'UPDATE_PREFERENCES'; payload: Partial<MovePreferences> }
  | { type: 'SET_RESULTS'; payload: LocationResult[] }
  | { type: 'ADD_LOCATION'; payload: LocationSuggestion }
  | { type: 'REMOVE_LOCATION'; payload: string } // Remove by display_name
  | { type: 'RESET' };

const initialState: FlowState = {
  step: 'preferences',
  preferences: {
    temperature: 'warm',
    precipitation: 'dry',
    locations: [],
    priority: 'year-round-comfort'
  },
  results: [],
  validatedLocations: []
};

function flowReducer(state: FlowState, action: FlowAction): FlowState {
  console.log('🔄 Reducer called:', action.type, 'Current state:', state.step, 'Results:', state.results.length);
  
  switch (action.type) {
    case 'SET_STEP':
      const newStepState = { ...state, step: action.payload };
      console.log('✅ SET_STEP result:', newStepState);
      return newStepState;
      
    case 'UPDATE_PREFERENCES':
      const newPreferences = { ...state.preferences, ...action.payload };
      const newPrefState = { ...state, preferences: newPreferences };
      console.log('✅ UPDATE_PREFERENCES result:', newPrefState);
      return newPrefState;
      
    case 'SET_RESULTS':
      const newResultsState = { 
        ...state, 
        results: [...action.payload], // Ensure new array reference
        step: 'results' as const
      };
      console.log('✅ SET_RESULTS result:', newResultsState, 'Results count:', newResultsState.results.length);
      return newResultsState;

    case 'ADD_LOCATION':
      const newLocation = action.payload;
      // Avoid duplicates
      if (state.validatedLocations.some(loc => loc.display_name === newLocation.display_name)) {
        return state;
      }
      const addLocationState = {
        ...state,
        validatedLocations: [...state.validatedLocations, newLocation],
        preferences: {
          ...state.preferences,
          locations: [...state.preferences.locations, newLocation.display_name]
        }
      };
      console.log('✅ ADD_LOCATION result:', addLocationState);
      return addLocationState;

    case 'REMOVE_LOCATION':
      const locationToRemove = action.payload;
      const removeLocationState = {
        ...state,
        validatedLocations: state.validatedLocations.filter(loc => loc.display_name !== locationToRemove),
        preferences: {
          ...state.preferences,
          locations: state.preferences.locations.filter(name => name !== locationToRemove)
        }
      };
      console.log('✅ REMOVE_LOCATION result:', removeLocationState);
      return removeLocationState;
      
    case 'RESET':
      console.log('✅ RESET to initial state');
      return { ...initialState }; // Ensure new object reference
      
    default:
      console.warn('❌ Unknown action type:', (action as any).type);
      return state;
  }
}

export const PlanningMoveFlow: React.FC<PlanningMoveFlowProps> = ({ onBack, onLoading, onError }) => {
  console.log('🏗️ PlanningMoveFlow component mounting/rendering');
  const [state, dispatch] = useReducer(flowReducer, initialState);
  const { step, preferences, results, validatedLocations } = state;

  // Debug logging
  console.log('📊 PlanningMoveFlow render - step:', step, 'results:', results.length, 'state:', state);

  // Scroll to top whenever step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);


  const handlePreferencesNext = (newPreferences: PerfectConditions & { priority: MovePreferences['priority'] }) => {
    dispatch({ type: 'UPDATE_PREFERENCES', payload: newPreferences });
    dispatch({ type: 'SET_STEP', payload: 'locations' });
  };

  const handleLocationsNext = async () => {
    console.log('handleLocationsNext called');
    console.log('Current validatedLocations:', validatedLocations);
    console.log('Current preferences:', preferences);
    
    if (validatedLocations.length === 0) {
      onError('Please add at least one location to compare');
      return;
    }

    onLoading(true);
    onError(null);

    try {
      console.log('Making API call with validated locations:', validatedLocations);
      const response = await movePlanningApi.compareLocations(preferences, validatedLocations);
      console.log('API response received:', response);
      
      // Validate response data before setting state
      if (response && response.data && Array.isArray(response.data)) {
        console.log('Setting results with', response.data.length, 'locations');
        dispatch({ type: 'SET_RESULTS', payload: response.data });
        console.log('State update dispatched - results:', response.data.length);
      } else {
        console.error('Invalid response format:', response);
        onError('Invalid response from server');
      }
      
    } catch (error) {
      console.error('Location comparison error:', error);
      onError(error instanceof Error ? error.message : 'Failed to analyze locations');
    } finally {
      onLoading(false);
    }
  };

  const handleShowBestLocations = async () => {
    console.log('handleShowBestLocations called');
    console.log('Current preferences:', preferences);
    
    onLoading(true);
    onError(null);

    try {
      console.log('Making API call for best locations with preferences:', preferences);
      const response = await movePlanningApi.compareLocations(preferences, []);
      console.log('Best locations API response received:', response);
      
      // Validate response data before setting state
      if (response && response.data && Array.isArray(response.data)) {
        console.log('Setting results with', response.data.length, 'locations');
        
        // Atomic state update: both results and step in single dispatch
        dispatch({ type: 'SET_RESULTS', payload: response.data });
        
        console.log('State update dispatched - results:', response.data.length);
      } else {
        console.error('Invalid response format:', response);
        onError('Invalid response from server');
      }
      
    } catch (error) {
      console.error('Best locations error:', error);
      onError(error instanceof Error ? error.message : 'Failed to get best locations');
    } finally {
      onLoading(false);
    }
  };

  if (step === 'results') {
    console.log('Rendering results step with:', results);
    console.log('Results length:', results?.length);
    
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Location Comparison Results</h2>
          <button
            onClick={() => dispatch({ type: 'SET_STEP', payload: 'locations' })}
            className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to locations
          </button>
        </div>
        
        <LocationComparison results={results} preferences={preferences} />
      </div>
    );
  }

  if (step === 'locations') {
    return (
      <LocationsStep 
        onBack={() => dispatch({ type: 'SET_STEP', payload: 'preferences' })} 
        onNext={handleLocationsNext} 
        onShowBestLocations={handleShowBestLocations}
        validatedLocations={validatedLocations}
        onLocationAdd={(location) => dispatch({ type: 'ADD_LOCATION', payload: location })}
        onLocationRemove={(locationName) => dispatch({ type: 'REMOVE_LOCATION', payload: locationName })}
      />
    );
  }

  return <PreferencesStep onBack={onBack} onNext={handlePreferencesNext} />;
};

// Preferences Step Component
interface PreferencesStepProps {
  onBack: () => void;
  onNext: (preferences: PerfectConditions & { priority: MovePreferences['priority'] }) => void;
}

const PreferencesStep: React.FC<PreferencesStepProps> = ({ onBack, onNext }) => {
  const [temperature, setTemperature] = useState<PerfectConditions['temperature']>('warm');
  const [precipitation, setPrecipitation] = useState<PerfectConditions['precipitation']>('dry');
  const [priority, setPriority] = useState<MovePreferences['priority']>('year-round-comfort');

  const temperatureOptions = [
    { value: 'hot' as const, label: 'Hot', description: '80°F+ most days', icon: '🔥' },
    { value: 'warm' as const, label: 'Warm', description: '65-80°F most days', icon: '☀️' },
    { value: 'cool' as const, label: 'Cool', description: '45-65°F most days', icon: '🌥️' },
    { value: 'cold' as const, label: 'Cold', description: 'Below 40°F often', icon: '❄️' },
    { value: 'freezing' as const, label: 'Freezing', description: 'Below 32°F regularly', icon: '🧊' }
  ];

  const precipitationOptions = [
    { value: 'dry' as const, label: 'Dry Climate', description: 'Minimal rain or snow, less than 50 days/year', icon: '🌵' },
    { value: 'occasional-rain' as const, label: 'Occasional Precipitation', description: 'Moderate rain or snow, 50-100 days/year', icon: '🌦️' },
    { value: 'regular-rain' as const, label: 'Regular Precipitation', description: 'Frequent rain or snow, 100+ days/year', icon: '🌧️' }
  ];

  const priorityOptions = [
    { value: 'year-round-comfort' as const, label: 'Year-round Comfort', description: 'Consistent pleasant weather all year' },
    { value: 'avoid-extremes' as const, label: 'Avoid Extremes', description: 'No very hot summers or cold winters' },
    { value: 'specific-seasons' as const, label: 'Great Seasons', description: 'Amazing weather during specific seasons' }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Home className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Planning a Move</h2>
        </div>
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Thermometer className="h-5 w-5 mr-2 text-red-500" />
          What's your ideal temperature?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {temperatureOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setTemperature(option.value)}
              className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                temperature === option.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="text-2xl mb-2">{option.icon}</div>
              <div className="font-medium text-gray-900 dark:text-white">{option.label}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{option.description}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <CloudRain className="h-5 w-5 mr-2 text-blue-500" />
          How much precipitation do you want?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {precipitationOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setPrecipitation(option.value)}
              className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                precipitation === option.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="text-2xl mb-2">{option.icon}</div>
              <div className="font-medium text-gray-900 dark:text-white">{option.label}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{option.description}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="card mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Calendar className="h-5 w-5 mr-2 text-purple-500" />
          What matters most to you?
        </h3>
        <div className="space-y-3">
          {priorityOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setPriority(option.value)}
              className={`w-full p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                priority === option.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="font-medium text-gray-900 dark:text-white">{option.label}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{option.description}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => onNext({ temperature, precipitation, priority })}
          className="btn-primary"
        >
          Next: Choose Locations
        </button>
      </div>
    </div>
  );
};

// Locations Step Component
interface LocationsStepProps {
  onBack: () => void;
  onNext: () => void;
  onShowBestLocations: () => void;
  validatedLocations: LocationSuggestion[];
  onLocationAdd: (location: LocationSuggestion) => void;
  onLocationRemove: (locationName: string) => void;
}

const LocationsStep: React.FC<LocationsStepProps> = ({ 
  onBack, 
  onNext, 
  onShowBestLocations, 
  validatedLocations, 
  onLocationAdd, 
  onLocationRemove 
}) => {
  const popularLocationSuggestions: LocationSuggestion[] = [
    { display_name: 'San Diego, CA', name: 'San Diego', state: 'California', country: 'US', lat: 32.7157, lon: -117.1611, type: 'city', importance: 0.8 },
    { display_name: 'Austin, TX', name: 'Austin', state: 'Texas', country: 'US', lat: 30.2672, lon: -97.7431, type: 'city', importance: 0.7 },
    { display_name: 'Denver, CO', name: 'Denver', state: 'Colorado', country: 'US', lat: 39.7392, lon: -104.9903, type: 'city', importance: 0.7 },
    { display_name: 'Portland, OR', name: 'Portland', state: 'Oregon', country: 'US', lat: 45.5152, lon: -122.6784, type: 'city', importance: 0.6 },
    { display_name: 'Nashville, TN', name: 'Nashville', state: 'Tennessee', country: 'US', lat: 36.1627, lon: -86.7816, type: 'city', importance: 0.6 },
    { display_name: 'Raleigh, NC', name: 'Raleigh', state: 'North Carolina', country: 'US', lat: 35.7796, lon: -78.6382, type: 'city', importance: 0.5 },
    { display_name: 'Phoenix, AZ', name: 'Phoenix', state: 'Arizona', country: 'US', lat: 33.4484, lon: -112.0740, type: 'city', importance: 0.7 },
    { display_name: 'Seattle, WA', name: 'Seattle', state: 'Washington', country: 'US', lat: 47.6062, lon: -122.3321, type: 'city', importance: 0.8 }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <MapPin className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Choose Locations to Compare</h2>
        </div>
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Search and add locations you're considering
        </h3>
        
        <LocationTypeahead
          onLocationAdd={onLocationAdd}
          placeholder="Search for cities (e.g., Austin, TX or London, UK)"
          className="mb-4"
        />

        {validatedLocations.length > 0 && (
          <div className="space-y-2 mb-4">
            <h4 className="font-medium text-gray-700 dark:text-gray-300">Your locations:</h4>
            <div className="flex flex-wrap gap-2">
              {validatedLocations.map((location) => (
                <div
                  key={location.display_name}
                  className="flex items-center bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-full text-sm"
                >
                  <span className="mr-1">{location.type === 'city' ? '🏙️' : location.type === 'town' ? '🏘️' : '📍'}</span>
                  <span>{location.display_name}</span>
                  <button
                    onClick={() => onLocationRemove(location.display_name)}
                    className="ml-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Popular locations:</h4>
          <div className="flex flex-wrap gap-2">
            {popularLocationSuggestions.map((location) => {
              const isAlreadyAdded = validatedLocations.some(loc => loc.display_name === location.display_name);
              return (
                <button
                  key={location.display_name}
                  onClick={() => {
                    if (!isAlreadyAdded) {
                      onLocationAdd(location);
                    }
                  }}
                  disabled={isAlreadyAdded}
                  className={`px-3 py-1 rounded-full text-sm transition-colors flex items-center space-x-1 ${
                    isAlreadyAdded
                      ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <MapPin className="h-3 w-3" />
                  <span>{location.display_name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
          Add locations to compare specific places, or see the best locations worldwide for your preferences.
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={onNext}
            disabled={validatedLocations.length === 0}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Compare My Locations ({validatedLocations.length})
          </button>
          
          <div className="text-center text-gray-400 dark:text-gray-500 self-center">or</div>
          
          <button
            onClick={onShowBestLocations}
            className="btn-secondary"
          >
            Show Best Locations Worldwide
          </button>
        </div>
      </div>
    </div>
  );
};
